import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { SliderNumericField } from '@/components/ui/numeric-input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { VideoFeatures } from '@/store/useVideoFeatures';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';
import { LOGO_POSITIONS } from './logo-utils';
import { cn } from '@/lib/utils';
import { Image, Link2, Move, Upload, Settings, AlertCircle } from 'lucide-react';
import { UploadControl } from '@/components/UploadControl';
import { HeroCard } from '@/components/ui/HeroCard';
import { videoService } from '@/services/videoService';
import { useToast } from '@/hooks/use-toast';

import './logo-animations.css';

interface LogoConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const LogoConfig: React.FC<LogoConfigProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  const setTempFile = useVideoFeaturesStore((state) => state.setTempFile);
  const clearTempFile = useVideoFeaturesStore((state) => state.clearTempFile);
  const { toast } = useToast();

  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [fileInfo, setFileInfo] = useState<{ name: string; size: string } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // URL validation state
  const [urlError, setUrlError] = useState<string | null>(null);

  // Normaliza URL automaticamente (adiciona https:// se necessário)
  const normalizeUrl = (input: string): string => {
    if (!input || input.trim() === '') return '';
    
    let url = input.trim();
    
    // Se já tem protocolo, retorna como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Se começa com www., adiciona https://
    if (url.startsWith('www.')) {
      return `https://${url}`;
    }
    
    // Se parece com domínio (tem pelo menos um ponto e sem espaços), adiciona https://
    if (url.includes('.') && !url.includes(' ')) {
      return `https://${url}`;
    }
    
    return url;
  };

  // URL validation function
  const validateUrl = (url: string) => {
    if (!url || url.trim() === '') {
      setUrlError(null);
      return;
    }
    
    const trimmed = url.trim();
    
    // Se já tem protocolo válido
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      try {
        new URL(trimmed);
        setUrlError(null);
      } catch {
        setUrlError(translateInterfaceText("URL inválida"));
      }
      return;
    }
    
    // Se começa com www. ou tem formato de domínio válido
    if (trimmed.startsWith('www.') || (trimmed.includes('.') && !trimmed.includes(' '))) {
      setUrlError(null);
      return;
    }
    
    // Não parece URL válida
    setUrlError(translateInterfaceText("Digite um domínio válido. Ex: seusite.com"));
  };

  const updateLogo = <K extends keyof NonNullable<VideoFeatures['logo']>>(
    key: K,
    value: NonNullable<VideoFeatures['logo']>[K]
  ) => {
    setFeature('logo', {
      ...features.logo,
      enabled: features.logo?.enabled ?? true,
      url: features.logo?.url ?? '',
      imageUrl: features.logo?.imageUrl ?? '',
      position: features.logo?.position ?? 'top-left',
      size: features.logo?.size ?? 10,
      opacity: features.logo?.opacity ?? 1,
      width: features.logo?.width ?? 100,
      linkTarget: 'new', // Always open in new tab
      [key]: value,
    });
  };


  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg'];
    const maxSizeMB = 1;

    if (!acceptedFormats.includes(file.type)) {
      return { valid: false, error: translateInterfaceText("Formato não suportado. Use PNG ou JPG.") };
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: `Arquivo maior que ${maxSizeMB}MB` };
    }

    return { valid: true };
  };

  const handleFileSelect = (file: File) => {
    const validation = validateFile(file);

    if (!validation.valid) {
      setUploadError(validation.error || 'Erro ao processar arquivo');
      return;
    }

    setUploadError(null);
    setIsUploading(true);
    setImageLoaded(false);

    // Store file in Zustand for later upload
    setTempFile('logoFile', file);

    const reader = new FileReader();
    reader.onload = (event) => {
      setTimeout(() => {
        if (event.target?.result) {
          const imageUrl = event.target.result.toString();
          setFeature('logo', {
            ...features.logo,
            imageUrl,
            url: imageUrl,
            position: features.logo?.position ?? 'top-left',
            opacity: features.logo?.opacity ?? 1,
            size: features.logo?.size ?? 10,
            width: 100,
            enabled: true,
            linkTarget: 'new',
          });
          setFileInfo({
            name: file.name,
            size: formatFileSize(file.size)
          });
        }
        setIsUploading(false);
      }, 400);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    const videoId = features.videoId;
    if (!videoId) {
      toast({
        title: translateInterfaceText("Erro"),
        description: translateInterfaceText("ID do vídeo não encontrado"),
        variant: "destructive"
      });
      return;
    }

    try {
      await videoService.removeLogo(videoId);
      setFileInfo(null);
      setImageLoaded(false);
      setFeature('logo', {
        enabled: false,
        url: '',
        imageUrl: '',
        position: 'top-left',
        size: 10,
        opacity: 1,
        width: 100,
        linkTarget: 'new',
      });
      clearTempFile('logoFile');
      toast({
        title: translateInterfaceText("Logo removida"),
        description: translateInterfaceText("A logo foi removida com sucesso"),
      });
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({
        title: translateInterfaceText("Erro ao remover logo"),
        description: translateInterfaceText("Ocorreu um erro ao remover a logo"),
        variant: "destructive"
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const currentImageUrl = features.logo?.imageUrl || features.logo?.url;

  return (
    <div className="flex flex-col h-full">
      {/* Header Card sem switch - mantém estrutura visual do HeroCard */}
      <div className="px-6 py-4">
        <div className="flex items-center gap-3 rounded-[12px] p-3.5 bg-white border border-[#E4E7EC] transition-all duration-200 hover:border-[#D0D5DD]">
          {/* Icon Wrapper */}
          <div className="flex-shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center bg-[#EEF4FF] text-[#1D4FFF]">
            <div className="w-5 h-5">
              <Image className="w-full h-full" strokeWidth={2} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight truncate">
              {translateInterfaceText("Logo")}</h4>
            <p className="text-[12px] font-normal text-[#6B7280] leading-snug mt-1 line-clamp-2">
              {translateInterfaceText("Exiba sua logomarca sobre o vídeo e aumente a visibilidade da sua marca.")}</p>
          </div>
        </div>
      </div>

      {/* Conteúdo - sempre visível */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Tabs defaultValue="config" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 h-[44px] bg-white border border-[#E4E7EC] rounded-lg overflow-hidden">
              <TabsTrigger 
                value="config"
                className="data-[state=active]:bg-[#1F6BFF] data-[state=active]:text-white data-[state=active]:font-semibold rounded-none hover:bg-[#1F6BFF]/10 hover:text-[#1F6BFF] data-[state=active]:hover:bg-[#1F6BFF] data-[state=active]:hover:text-white transition-all duration-200"
              >
                {translateInterfaceText("Configurações")}</TabsTrigger>
              <TabsTrigger 
                value="appearance"
                className="data-[state=active]:bg-[#1F6BFF] data-[state=active]:text-white data-[state=active]:font-semibold rounded-none hover:bg-[#1F6BFF]/10 hover:text-[#1F6BFF] data-[state=active]:hover:bg-[#1F6BFF] data-[state=active]:hover:text-white transition-all duration-200"
              >
                {translateInterfaceText("Aparência")}</TabsTrigger>
            </TabsList>

            {/* TAB 1: Configurações */}
            <TabsContent value="config" className="space-y-5 mt-4">
              {/* Upload da Logo */}
              <Card className="border-0 shadow-sm bg-[#FAFBFC] hover:shadow-md transition-all duration-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Upload className="h-4 w-4 text-[#64748B]" />
                    <Label className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Upload da Logo")}</Label>
                  </div>
                  
                  <UploadControl
                    label=""
                    accept="image/png,image/jpeg,image/jpg"
                    maxSize={1}
                    preview={currentImageUrl}
                    onFileSelected={handleFileSelect}
                    onClear={handleRemoveLogo}
                    isLoading={isUploading}
                    isActive={features.logo?.enabled}
                  />
                  
                  {uploadError && (
                    <p className="text-xs text-destructive mt-2">
                      ⚠️ {uploadError}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Link */}
              <Card className="border-0 shadow-sm bg-[#FAFBFC] hover:shadow-md transition-all duration-200">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-[#64748B]" />
                    <Label className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Link de destino")}</Label>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        placeholder={translateInterfaceText("seusite.com")}
                        value={features.logo?.linkUrl || ''}
                        onChange={(e) => {
                          updateLogo('linkUrl', e.target.value);
                          validateUrl(e.target.value);
                        }}
                        onBlur={(e) => {
                          const normalized = normalizeUrl(e.target.value);
                          if (normalized !== e.target.value && normalized !== '') {
                            updateLogo('linkUrl', normalized);
                            validateUrl(normalized);
                          }
                        }}
                        className={cn(
                          "w-full transition-all duration-200",
                          urlError 
                            ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200" 
                            : "border-[#E4E7EC] hover:border-[#2563EB]/40 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                        )}
                      />
                      {urlError && (
                        <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                      )}
                    </div>
                    {urlError ? (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        {urlError}
                      </p>
                    ) : features.logo?.linkUrl && !features.logo.linkUrl.startsWith('http') ? (
                      <p className="text-xs text-[#1D4FFF] flex items-center gap-1">
                        {translateInterfaceText("→ Será salvo como: https://")}{features.logo.linkUrl}
                      </p>
                    ) : (
                      <p className="text-xs text-[#64748B]">
                        {translateInterfaceText("Aceita: seusite.com, www.seusite.com ou https://seusite.com")}</p>
                    )}
                  </div>

                </CardContent>
              </Card>

              {/* Posição e Tamanho */}
              <Card className="border-0 shadow-sm bg-[#FAFBFC] hover:shadow-md transition-all duration-200">
                <CardContent className="p-5 space-y-5">
                  <div className="flex items-center gap-2">
                    <Move className="h-4 w-4 text-[#64748B]" />
                    <Label className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Posição e Tamanho")}</Label>
                  </div>

                  {/* Position Grid */}
                  <div>
                    <Label className="text-xs text-[#64748B] mb-3 block">{translateInterfaceText("Posição no player")}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {LOGO_POSITIONS.map((pos) => (
                        <button
                          key={pos.value}
                          onClick={() => updateLogo('position', pos.value)}
                          className={cn(
                            "h-12 text-xs rounded-lg transition-all duration-200 ease-out font-medium",
                            "focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30",
                            features.logo?.position === pos.value
                              ? "border-2 border-[#1D4FFF] bg-[#F5F8FF] text-[#1D4FFF]"
                              : "bg-white text-[#64748B] border border-[#E6E6F0] hover:border-[#1D4FFF]/40 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:text-[#1D4FFF]"
                          )}
                        >
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB 2: Aparência */}
            <TabsContent value="appearance" className="space-y-5 mt-4">
              {/* Tamanho e Transparência */}
              <Card className="border-0 shadow-sm bg-[#FAFBFC] hover:shadow-md transition-all duration-200">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-[#64748B]" />
                    <Label className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Tamanho e Transparência")}</Label>
                  </div>
                  
                  {/* Size */}
                  <SliderNumericField
                    label={translateInterfaceText("Tamanho")}
                    value={features.logo?.size ?? 10}
                    onChange={(value) => updateLogo('size', value)}
                    min={10}
                    max={40}
                    step={1}
                    suffix="%"
                  />

                  {/* Opacity */}
                  <SliderNumericField
                    label={translateInterfaceText("Transparência")}
                    value={Math.round((features.logo?.opacity ?? 1) * 100)}
                    onChange={(value) => updateLogo('opacity', value / 100)}
                    min={0}
                    max={100}
                    step={1}
                    suffix="%"
                  />
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
    </div>
  );
};
